import React, { useState, useEffect, useLayoutEffect, useRef, useMemo } from 'react';
import { Filter, Check } from 'lucide-react';
import { applySort, columnOptions, isFilterActive } from '../../utils/listSearch';
import { columnFilterType } from '../../utils/columnFilterTypes';
import ColumnFilterMenu from './columnfilter/ColumnFilterMenu';

/**
 * Presentational data grid. Its column header menus are the STUI Column Filter
 * (components/common/columnfilter/ColumnFilterMenu) — the grid decides WHICH column's menu is open,
 * where, with what values and type; the menu owns everything inside it.
 *
 * It does NOT filter: `data` is rendered as given, already reduced by the page (search + column
 * filters — utils/listSearch or hooks/useListTrunk). When the grid filtered internally, the page
 * never learned which rows were removed, so sibling views, count chips and exports disagreed.
 *
 * Sort: pass `sortConfig` + `onSortChange` to control it (the page sorts the whole dataset before
 * paging, and the sort survives a view toggle). Without them the grid keeps its own sort state and
 * sorts `data` itself — fine for grids that are not paged and never unmount.
 *
 * Options: `allData` (the search-filtered set) feeds each menu's values, cascading across the other
 * columns' filters; `optionsProvider(colKey)` overrides that for server-backed grids and may return
 * values, { values, truncated }, or a Promise of either (fetched when the menu opens).
 *
 * Column props for the menu: `filterType` ('text' | 'number' | 'date'; inferred from the data when
 * absent — utils/columnFilterTypes), `filterable: false` (sort only), `filterable: 'text'`
 * (conditions only, no value list — long text).
 */

const FilterableDataGrid = ({
  data,
  allData, // Full unfiltered data for calculating filter options
  columns: initialColumns,
  onRowClick,
  onRowDoubleClick,
  filters,
  setFilters,
  // Column customization props (external state from header)
  columnOrder: externalColumnOrder,
  hiddenColumns: externalHiddenColumns,
  columnWidths: externalColumnWidths,
  onColumnResize,
  // Edit mode props
  editModeEnabled = false,
  editedData = {},           // { rowId: { fieldName: value } }
  renderEditableCell,        // (row, column, value, isEdited) => JSX
  // Delete mode props
  deleteModeEnabled = false,
  selectedForDelete = new Set(),
  onToggleSelectRow,
  onSelectAllOnPage,
  // Frozen columns
  frozenColumns = [],
  // Row class callback
  getRowClassName,           // (row) => string — extra class(es) for the <tr>
  // Grouped column headers (optional). When provided, renders a second header
  // row above the column labels with cells spanning grouped columns.
  //   sectionGroups={[
  //     { label: 'Actual IO Counts', keys: ['act_AI','act_AO', ...] },
  //     { label: 'Card Density',     keys: ['den_AI','den_AO', ...] },
  //   ]}
  // Columns not listed in any group render as blank spacers in the section row.
  sectionGroups = null,
  // Controlled sort ({ key, direction }). Omit both for internal sort state.
  sortConfig: controlledSort,
  onSortChange,
  // (colKey) => string[] — overrides the options computed from allData
  optionsProvider,
}) => {
  // Use allData for filter options if provided, otherwise fall back to data
  const optionsData = allData || data;
  const [openFilter, setOpenFilter] = useState(null);
  const [internalSort, setInternalSort] = useState({ key: null, direction: null });
  const sortControlled = controlledSort !== undefined;
  const sortConfig = (sortControlled ? controlledSort : internalSort) || { key: null, direction: null };
  const [hoveredColumn, setHoveredColumn] = useState(null);
  const headerRefs = useRef({});
  const [menuAnchor, setMenuAnchor] = useState(null);

  // Column customization state - use external if provided, otherwise internal
  const [internalColumnOrder, setInternalColumnOrder] = useState(() => initialColumns.map(col => col.key));
  const [internalHiddenColumns] = useState(new Set());
  const [internalColumnWidths, setInternalColumnWidths] = useState(() => {
    const widths = {};
    initialColumns.forEach(col => {
      widths[col.key] = col.width || 150;
    });
    return widths;
  });

  // Column resize state
  const [resizing, setResizing] = useState(null); // { key, startX, startWidth }
  const tableRef = useRef(null);

  // Rows keep their view-mode height in edit mode, so switching modes never moves a row and the
  // record the user found stays under their eye. Edit cells already match a one-line view cell
  // (STUI .editable-cell); this covers rows whose text wraps in view, where the editor is one
  // line. The heights are read from the outgoing view DOM during the render that turns edit mode
  // on, before React commits it. Rows the edit session has not seen (a new page) size naturally.
  const viewRowHeights = useRef(null);
  const wasEditing = useRef(editModeEnabled);
  if (wasEditing.current !== editModeEnabled) {
    wasEditing.current = editModeEnabled;
    viewRowHeights.current = null;
    if (editModeEnabled && tableRef.current) {
      viewRowHeights.current = new Map(
        [...tableRef.current.querySelectorAll('tbody > tr[data-row-key]')]
          .map(tr => [tr.dataset.rowKey, tr.getBoundingClientRect().height]));
    }
  }

  // Use external state if provided (and non-empty), otherwise use internal
  const columnOrder = (externalColumnOrder?.length > 0) ? externalColumnOrder : internalColumnOrder;
  const hiddenColumns = (externalHiddenColumns?.length > 0) ? new Set(externalHiddenColumns) : internalHiddenColumns;
  const columnWidths = externalColumnWidths || internalColumnWidths;

  // Update internal column order when initialColumns change (only if using internal state)
  useEffect(() => {
    if (!externalColumnOrder?.length) {
      setInternalColumnOrder(prev => {
        const newKeys = initialColumns.map(col => col.key);
        const prevSet = new Set(prev);
        const newSet = new Set(newKeys);
        // If the set of keys changed, reset to initialColumns order (respects upstream ordering)
        const keysChanged = newKeys.some(k => !prevSet.has(k)) || prev.some(k => !newSet.has(k));
        if (!keysChanged) return prev;
        return newKeys;
      });
    }
  }, [initialColumns, externalColumnOrder]);

  // Get visible columns in current order
  const visibleColumns = columnOrder
    .filter(key => !hiddenColumns.has(key))
    .map(key => initialColumns.find(col => col.key === key))
    .filter(Boolean);


  // Column resize handlers
  const handleResizeStart = (e, colKey) => {
    e.preventDefault();
    e.stopPropagation();
    const startWidth = columnWidths[colKey] || 150;
    setResizing({ key: colKey, startX: e.clientX, startWidth });
  };

  useEffect(() => {
    if (!resizing) return;

    const handleMouseMove = (e) => {
      const diff = e.clientX - resizing.startX;
      const newWidth = Math.max(50, resizing.startWidth + diff); // Min width 50px

      if (onColumnResize) {
        onColumnResize(resizing.key, newWidth);
      } else {
        setInternalColumnWidths(prev => ({
          ...prev,
          [resizing.key]: newWidth
        }));
      }
    };

    const handleMouseUp = () => {
      setResizing(null);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [resizing, onColumnResize]);

  // Each column's filter type (text / number / date / none), inferred from a sample of its values
  // unless the column declares one. Date columns also sort by calendar day.
  const columnTypes = useMemo(() => {
    const sample = (optionsData || []).slice(0, 200);
    const types = {};
    initialColumns.forEach(col => { types[col.key] = columnFilterType(col, sample.map(r => r[col.key])); });
    return types;
  }, [initialColumns, optionsData]);
  const typedColumns = useMemo(
    () => initialColumns.map(c => (columnTypes[c.key] === 'date' && !c.filterType ? { ...c, filterType: 'date' } : c)),
    [initialColumns, columnTypes]
  );

  // Provider results per column: { loading, values, truncated }. Filled when a menu opens.
  const [providedOptions, setProvidedOptions] = useState({});
  const normalizeOptions = (r) => ({
    loading: false,
    values: Array.isArray(r) ? r : (r?.values || []),
    truncated: !Array.isArray(r) && !!r?.truncated,
  });
  const loadProvidedOptions = (colKey) => {
    if (!optionsProvider) return;
    const result = optionsProvider(colKey);
    if (result && typeof result.then === 'function') {
      setProvidedOptions(prev => ({ ...prev, [colKey]: { loading: true, values: prev[colKey]?.values || [], truncated: false } }));
      result
        .then(r => setProvidedOptions(prev => ({ ...prev, [colKey]: normalizeOptions(r) })))
        .catch(() => setProvidedOptions(prev => ({ ...prev, [colKey]: { loading: false, values: [], truncated: false } })));
    } else {
      setProvidedOptions(prev => ({ ...prev, [colKey]: normalizeOptions(result) }));
    }
  };

  const openColumn = openFilter ? initialColumns.find(c => c.key === openFilter) : null;
  const openType = openFilter ? columnTypes[openFilter] : null;
  const openHasList = !!openColumn && openType !== 'none' && openColumn.filterable !== 'text';

  // The open column's values, computed ONCE per open — not per keystroke or per checkbox render.
  const openColumnOptions = useMemo(() => {
    if (!openHasList) return [];
    return (optionsProvider ? providedOptions[openFilter]?.values : columnOptions(optionsData, filters, openFilter)) || [];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openFilter, openHasList, optionsData, filters, providedOptions, optionsProvider]);

  const openFilterDropdown = (colKey) => {
    const header = headerRefs.current[colKey];
    // The menu places itself from its own measured size; it only needs the header's rect.
    if (header) setMenuAnchor(header.getBoundingClientRect());
    setOpenFilter(colKey);
    const col = initialColumns.find(c => c.key === colKey);
    if (columnTypes[colKey] !== 'none' && col?.filterable !== 'text') loadProvidedOptions(colKey);
  };

  const handleSort = (colKey, direction) => {
    const next = { key: colKey, direction };
    if (sortControlled) onSortChange?.(next);
    else setInternalSort(next);
    setOpenFilter(null);
  };

  // Controlled: the page already sorted (before paging). Uncontrolled: sort what was handed in.
  // A column may supply sortValue to sort by something other than its text (see applySort).
  const sortedData = sortControlled ? data : applySort(data, sortConfig, typedColumns);

  // eslint-disable-next-line no-unused-vars
  const hasActiveFilters = filters && Object.values(filters).some(isFilterActive);

  // Calculate total table width from column widths (add 40px for delete checkbox column if enabled)
  const tableWidth = visibleColumns.reduce((sum, c) => sum + (columnWidths[c.key] || c.width || 150), 0) + (deleteModeEnabled ? 40 : 0);

  useLayoutEffect(() => {
    if (!tableRef.current) return;
    tableRef.current.style.width = `${tableWidth}px`;
    const cols = tableRef.current.querySelectorAll('col');
    const offset = deleteModeEnabled ? 1 : 0;
    visibleColumns.forEach((col, idx) => {
      if (cols[idx + offset]) cols[idx + offset].style.width = `${columnWidths[col.key] || col.width || 150}px`;
    });
  }, [tableWidth, columnWidths, visibleColumns, deleteModeEnabled]);

  // Compute left offsets for sticky frozen columns
  const frozenSet = new Set(frozenColumns);
  const frozenLeftOffsets = {};
  if (frozenSet.size > 0) {
    let leftAccum = deleteModeEnabled ? 40 : 0;
    visibleColumns.forEach(col => {
      if (frozenSet.has(col.key)) frozenLeftOffsets[col.key] = leftAccum;
      leftAccum += columnWidths[col.key] || col.width || 150;
    });
  }

  // Check if all visible rows are selected for delete
  const allOnPageSelected = deleteModeEnabled && sortedData.length > 0 && sortedData.every(row => selectedForDelete.has(row.id));

  return (
    <div className={`stui-data-grid-container ${resizing ? 'is-resizing' : ''}`}>
      <div className="stui-data-grid-scroll-area">
      <table ref={tableRef} className={`stui-data-grid${editModeEnabled ? ' edit-mode' : ''}${deleteModeEnabled ? ' delete-mode' : ''}`}>
        <colgroup>
          {deleteModeEnabled && <col className="stui-col-delete" />}
          {visibleColumns.map(col => (
            <col key={col.key} />
          ))}
        </colgroup>
        <thead>
          {sectionGroups && sectionGroups.length > 0 && (() => {
            // Build the top row by walking visibleColumns and merging adjacent
            // columns that belong to the same group. Columns not in any group
            // become blank spacer cells.
            const keyToGroup = new Map();
            sectionGroups.forEach(g => (g.keys || []).forEach(k => keyToGroup.set(k, g)));
            const topCells = [];
            let curGroup = null, curSpan = 0;
            visibleColumns.forEach(col => {
              const g = keyToGroup.get(col.key) ?? null;
              if (g === curGroup) {
                curSpan++;
              } else {
                if (curSpan > 0) topCells.push({ group: curGroup, span: curSpan });
                curGroup = g;
                curSpan = 1;
              }
            });
            if (curSpan > 0) topCells.push({ group: curGroup, span: curSpan });
            return (
              <tr className="stui-data-grid-section-row">
                {deleteModeEnabled && <th className="stui-data-grid-section-spacer" />}
                {topCells.map((cell, i) => (
                  <th
                    key={i}
                    colSpan={cell.span}
                    className={cell.group ? 'stui-data-grid-section-cell' : 'stui-data-grid-section-spacer'}
                  >
                    {cell.group?.label || ''}
                  </th>
                ))}
              </tr>
            );
          })()}
          <tr>
            {deleteModeEnabled && (
              <th className="stui-delete-col">
                <input
                  type="checkbox"
                  className="stui-checkbox-input stui-checkbox-input-danger"
                  checked={allOnPageSelected}
                  onChange={onSelectAllOnPage}
                  title="Select all on page"
                />
              </th>
            )}
            {visibleColumns.map(col => {
              const hasFilter = isFilterActive(filters[col.key]);
              const isSorted = sortConfig.key === col.key;
              const isHovered = hoveredColumn === col.key;
              const isVertical = col.headerVertical;
              const isFrozen = frozenSet.has(col.key);
              const thClasses = [
                isVertical ? 'header-rotate-90' : '',
                'filterable-header',
                isFrozen ? 'frozen' : ''
              ].filter(Boolean).join(' ');
              const frozenStyle = isFrozen ? { position: 'sticky', left: frozenLeftOffsets[col.key], zIndex: 4 } : undefined;
              return (
                <th
                  key={col.key}
                  ref={el => headerRefs.current[col.key] = el}
                  className={thClasses}
                  style={frozenStyle}
                  onClick={() => openFilterDropdown(col.key)}
                  onMouseEnter={() => setHoveredColumn(col.key)}
                  onMouseLeave={() => setHoveredColumn(null)}
                >
                  <span className={`column-label ${isVertical ? 'header-content' : ''}`}>{col.label}</span>
                  <Filter
                    size={12}
                    className={`header-filter-icon ${isHovered || hasFilter || isSorted ? 'visible' : ''} ${hasFilter || isSorted ? 'active' : ''}`}
                  />
                  <div
                    className="stui-column-resize-handle"
                    onMouseDown={(e) => handleResizeStart(e, col.key)}
                  />
                </th>
              );
            })}
          </tr>
        </thead>
        <tbody>
          {sortedData.map((row, idx) => {
            const isSelectedForDelete = deleteModeEnabled && selectedForDelete.has(row.id);
            const extraClass = getRowClassName ? getRowClassName(row) : '';
            const rowKey = String(row.id ?? idx);
            const keptHeight = editModeEnabled ? viewRowHeights.current?.get(rowKey) : undefined;
            return (
            <tr
              key={row.id || idx}
              data-row-key={rowKey}
              style={keptHeight ? { height: keptHeight } : undefined}
              onClick={() => onRowClick?.(row)}
              onDoubleClick={() => onRowDoubleClick?.(row)}
              className={[onRowClick || onRowDoubleClick ? 'stui-clickable' : '', isSelectedForDelete ? 'selected-for-delete' : '', extraClass].filter(Boolean).join(' ') || undefined}
            >
              {deleteModeEnabled && (
                <td className="stui-delete-col">
                  <input
                    type="checkbox"
                    className="stui-checkbox-input stui-checkbox-input-danger"
                    checked={isSelectedForDelete}
                    onChange={() => onToggleSelectRow?.(row.id)}
                  />
                </td>
              )}
              {visibleColumns.map(col => {
                const value = row[col.key];
                const rowId = row.id;
                const isFrozen = frozenSet.has(col.key);
                const frozenStyle = isFrozen ? { position: 'sticky', left: frozenLeftOffsets[col.key], zIndex: 1 } : undefined;
                const alignClass = col.alignment === 'right' ? ' stui-cell-right' : col.alignment === 'center' ? ' stui-cell-center' : '';
                const frozenClass = isFrozen ? ' frozen' : '';
                const isEdited = editedData[rowId]?.[col.fieldName] !== undefined;
                const displayValue = isEdited ? editedData[rowId][col.fieldName] : value;

                // Edit mode: use custom renderer if provided
                if (editModeEnabled && renderEditableCell) {
                  return (
                    <td key={col.key} className={`editable-cell${isEdited ? ' modified' : ''}${frozenClass}${alignClass}`} style={frozenStyle} onDoubleClick={e => e.stopPropagation()}>
                      {renderEditableCell(row, col, displayValue, isEdited)}
                    </td>
                  );
                }

                // Read mode: render boolean values as checkboxes
                if (typeof value === 'boolean') {
                  return (
                    <td key={col.key} className={`cell-boolean${frozenClass}${alignClass}`} style={frozenStyle}>
                      <div className="stui-boolean-display">
                        {value ? <Check size={16} className="stui-boolean-check" /> : null}
                      </div>
                    </td>
                  );
                }
                const renderedValue = col.renderCell ? col.renderCell(row, col, value) : col.formatter ? col.formatter(value) : value;
                const tdClass = (frozenClass + alignClass).trim() || undefined;
                return <td key={col.key} className={tdClass} style={frozenStyle}>{renderedValue}</td>;
              })}
            </tr>
            );
          })}
        </tbody>
      </table>
      </div>

      {openFilter && openColumn && (
        <ColumnFilterMenu
          key={openFilter}
          column={openColumn}
          type={openType}
          textOnly={openColumn.filterable === 'text'}
          filter={filters[openFilter]}
          options={openColumnOptions}
          optionsLoading={!!providedOptions[openFilter]?.loading}
          optionsTruncated={!!providedOptions[openFilter]?.truncated}
          sortConfig={sortConfig}
          onSort={direction => handleSort(openFilter, direction)}
          onApply={next => { setFilters(prev => ({ ...prev, [openFilter]: next })); setOpenFilter(null); }}
          onClose={() => setOpenFilter(null)}
          anchor={menuAnchor}
        />
      )}
    </div>
  );
};

export default FilterableDataGrid;
